import axios from 'axios'
import client, { ensureValidAccessToken, forceRefreshAccessToken } from './client'
import { getMyIndustryAgents } from './industries'
import type { Agent, GmailDraftAttachment } from '../types'

/**
 * Phase 2: Backend `/api/agents/` returns the legacy core-agent dict shape
 * `{id, name, ..., monthly_price: number}`. This adapter populates the
 * `agent_id` slug-alias and tags `agent_kind` so the unified frontend type
 * is fully populated even when the backend hasn't been migrated to emit
 * the unified shape yet.
 */
function adaptCoreAgent(raw: Record<string, unknown>): Agent {
  const slug = String(raw['id'] ?? raw['agent_id'] ?? '')
  const monthly = raw['monthly_price']
  const yearly = raw['yearly_price']
  return {
    id: slug,
    agent_id: slug,
    name: String(raw['name'] ?? ''),
    role: String(raw['role'] ?? ''),
    dept: String(raw['dept'] ?? ''),
    emoji: String(raw['emoji'] ?? ''),
    color: String(raw['color'] ?? ''),
    image_url: String(raw['image_url'] ?? ''),
    description: String(raw['description'] ?? ''),
    monthly_price: typeof monthly === 'number' ? monthly : parseFloat(String(monthly ?? '0')),
    yearly_price: typeof yearly === 'number' ? yearly : parseFloat(String(yearly ?? '0')),
    tagline: String(raw['tagline'] ?? ''),
    hero_headline: String(raw['hero_headline'] ?? ''),
    what_i_do: String(raw['what_i_do'] ?? ''),
    capabilities: (raw['capabilities'] as string[]) ?? [],
    use_cases: (raw['use_cases'] as Array<{ title: string; problem: string; solution: string }>) ?? [],
    industries_served: (raw['industries_served'] as string[]) ?? [],
    faqs: (raw['faqs'] as Array<{ question: string; answer: string }>) ?? [],
    agent_kind:
      (raw['agent_kind'] as 'core' | 'industry' | 'support' | undefined)
      ?? 'core',
  }
}

export async function listAgents(): Promise<Agent[]> {
  const { data } = await client.get('/api/agents/')
  return (data as Array<Record<string, unknown>>).map(adaptCoreAgent)
}

export async function getAgentById(agentId: string): Promise<Agent> {
  const { data } = await client.get(`/api/agents/${agentId}/public/`)
  return adaptCoreAgent(data as Record<string, unknown>)
}

export async function myAgents() {
  const { data } = await client.get('/api/agents/my-agents/')
  return data as { agent_ids: string[] }
}

export async function detectAgent(text: string): Promise<string> {
  const { data } = await client.post('/api/agents/detect/', { text })
  return data.agent_id as string
}

// Public endpoint — no auth required
export async function getPublicAgent(agentId: string): Promise<Agent> {
  const { data } = await axios.get(
    `${import.meta.env.VITE_API_URL}/api/agents/${agentId}/public/`
  )
  return adaptCoreAgent(data as Record<string, unknown>)
}

/**
 * Phase 2: fetch active SupportAgents (Rex, Nova) so the Sidebar can resolve
 * historical sessions whose primary_agent_id is a support slug.
 *
 * Backend doesn't yet expose a list endpoint for SupportAgent — Phase 1B-wiring
 * added the SupportAgent fallback to /api/agents/<slug>/public/ but no list
 * endpoint. We hit the known support slugs individually (cheap: 2 requests,
 * cached by the browser HTTP cache + React Query if present). Adding a real
 * list endpoint is a Phase 3 cleanup item.
 */
const SUPPORT_AGENT_SLUGS = ['rex', 'nova'] as const

export async function listSupportAgents(): Promise<Agent[]> {
  const results = await Promise.all(
    SUPPORT_AGENT_SLUGS.map((slug) =>
      getAgentById(slug)
        .then((a) => ({ ...a, agent_kind: 'support' as const }))
        .catch(() => null),
    ),
  )
  return results.filter((a): a is Agent => a !== null)
}

/**
 * Phase 2: single fetch that returns the user's complete agent set
 * (core + accessible-industry + support). Used by the Dashboard (and
 * Sidebar lookup downstream) so historical sessions for any agent type
 * resolve to the correct emoji/avatar/name.
 *
 * Industry agents come from getMyIndustryAgents which only returns ones
 * the user has access to. For a richer "all industry agents" view (e.g.
 * an unowned industry agent's session in history), Phase 3 will add a
 * broader endpoint. For now those rare cases fall back to the slug.
 */
export async function listAllAgents(): Promise<Agent[]> {
  const [core, industry, support] = await Promise.all([
    listAgents().catch(() => []),
    getMyIndustryAgents().catch(() => []),
    listSupportAgents().catch(() => []),
  ])
  return [...core, ...industry, ...support]
}

export interface StreamHandoff {
  agent_id: string
  agent_name: string
  agent_emoji: string
  agent_color?: string
  context_summary?: string
  content: string
}

export interface StreamDoneData {
  full_text: string
  agent_id: string
  agent_name: string
  agent_emoji: string
  agent_image_url?: string
  model_used: string
  is_lite_mode: boolean
  session_title: string
  trial_messages_remaining: number | null
  generated_image_urls?: string[]
  generated_document_urls?: string[]
  // Carried on the done event but reconciled lazily by a later session
  // refetch rather than consumed directly.
  handoffs?: StreamHandoff[]
  social_post_ids?: string[]
  social_reads?: unknown[]
  comment_reply_ids?: string[]
}

export interface ImageGeneratingData {
  prompt: string
}

export interface ImageCompleteData {
  url: string
  width: number
  height: number
}

export interface DocumentGeneratingData {
  title: string
}

export interface DocumentCompleteData {
  id: number
  url: string
  title: string
  file_name: string
  byte_size: number
}

// Stripe Connect — emitted when an agent calls stripe_propose_refund.
// The refund object is the full serialized RefundRequest from the backend
// (shape matches RefundRequest in ../types).
export interface StripeActionPendingData {
  kind: 'refund'
  refund_request_id: string
  refund: {
    id: string
    status: 'pending' | 'approved' | 'executed' | 'rejected' | 'failed'
    stripe_charge_id: string
    customer_name: string
    customer_email: string
    charge_amount_cents: number
    refund_amount_cents: number
    currency: string
    reason: string
    note: string
    ack_confirmed: boolean
    stripe_refund_id?: string
    execution_result?: Record<string, unknown>
    created_at: string
    executed_at?: string | null
    invoice_id?: number | null
    session_id?: string | null
    agent_name?: string | null
  }
}

export interface SpreadsheetGeneratingData {
  title: string
}

export interface SpreadsheetCompleteData {
  id: number
  url: string
  title: string
  file_name: string
  byte_size: number
}

export interface SocialPostDraftingData {
  caption: string
}

export interface SocialPostReadyData {
  id: string
  caption: string
  image_url?: string
  target_facebook_pages: string[]
  target_instagram_pages: string[]
  target_linkedin?: boolean
  status: string
  scheduled_at?: string | null
  schedule_error?: string
}

export interface ContentThemeProposalData {
  name: string
  focus: string
  audience: string
  goal: string
  angle: string
  posting_strategy: string
  content_ideas: string
}

export interface BrandKitProposalData {
  website: string
  description: string
  value_proposition: string
  brand_voice: string
  primary_color: string
  secondary_color: string
  tertiary_color: string
}

export interface AgentCustomizationProposalData {
  agent_id: string
  agent_name: string
  nickname: string
  personality: string
  response_length: string
  custom_instructions: string
}

export interface AdLibraryResultsData {
  keyword: string
  advertiser: string
  total: number
  ads: Array<{
    id: string
    advertiser_name: string
    advertiser_payer: string
    advertiser_url: string
    ad_type: string
    first_served_at: number | null
    latest_served_at: number | null
    impressions_from: number | null
    impressions_to: number | null
    countries: string[]
    languages: string[]
    audiences: string[]
    view_url: string
    is_restricted: boolean
  }>
}

export interface LinkedInEventCreatedData {
  event_id: string
  event_urn: string
  view_url: string
  name: string
  starts_at: string
  ends_at: string
  event_type: string
  is_published: boolean
}

export interface LinkedInEventListItemData {
  id: string
  event_urn: string
  name: string
  description: string
  starts_at: string
  ends_at: string
  event_type: string
  external_url: string
  is_published: boolean
  organizer_name: string
  view_url: string
}

export interface LinkedInEventsListData {
  events: LinkedInEventListItemData[]
}

// Phase 5D — LinkedIn Sponsored Content performance report SSE events.
// Loading → ready/failed; the `id` field is server-generated and pairs all
// three so the frontend can replace the placeholder card.
export interface LinkedInAdReportLoadingData {
  id: string
  time_window: string
}

export interface LinkedInAdReportCampaignData {
  id: string
  name: string
  status: string
  impressions: number
  clicks: number
  spend: number
  ctr: number
  cpc: number
  conversions: number
}

export interface LinkedInAdReportReadyData {
  id: string
  ad_account: { id: string; name: string; currency: string }
  time_window: { label: string; start_iso: string; end_iso: string }
  summary: {
    impressions: number
    clicks: number
    spend: number
    ctr: number
    cpc: number
    cpm: number
    conversions: number
    leads: number
  }
  campaigns: LinkedInAdReportCampaignData[]
  note?: string
}

export interface LinkedInAdReportFailedData {
  id: string
  error_message: string
  code: string
  actionable: boolean
  retry_after_seconds?: number
}

export interface SocialReadStartingData {
  read_type: string
  page_name?: string
  ig_username?: string
  target_id?: string
}

export interface SocialReadCompleteData {
  uuid: string
  read_type: string
  page_id?: string
  page_name?: string
  ig_username?: string
  target_id?: string
  payload?: Record<string, unknown>
  error?: { code?: number; error_subcode?: number; message?: string; type?: string } | null
}

export interface CommentReplyDraftingData {
  platform: string
  page_name?: string
  parent_author_name?: string
  reply_preview?: string
}

export interface CommentReplyReadyData {
  uuid: string
  platform: string
  parent_comment_id: string
  parent_comment_text?: string
  parent_author_name?: string
  reply_text: string
  status: string
  page_id?: string
  page_name?: string
  created_at?: string | null
  published_at?: string | null
}

// Phase 5a — Google Calendar SSE events.
export interface CalendarEventDraftingData {
  summary: string
}

export interface CalendarEventReadyData {
  id: string
  summary: string
  description: string
  location: string
  start_iso: string
  end_iso: string
  timezone: string
  attendees: Array<{ email: string; name?: string } | string>
  add_meet_link: boolean
  status: string
  gcal_event_id: string
  html_link: string
  hangout_link: string
  error_message: string
  created_at: string | null
  published_at: string | null
}

// Phase 5b — Gmail draft SSE events. Same drafting/ready pattern as
// CalendarEvent. The 'ready' payload mirrors the GmailDraft serialization
// shape returned by lifecycle endpoints.
export interface GmailDraftDraftingData {
  subject: string
}

export interface GmailDraftReadyData {
  id: string
  to_recipients: string[]
  cc_recipients: string[]
  bcc_recipients: string[]
  subject: string
  body_html: string
  body_text: string
  thread_id: string
  attachments?: GmailDraftAttachment[]
  status: string
  gmail_draft_id: string
  gmail_message_id: string
  deep_link?: string
  error_message: string
  rate_limited_until?: string | null
  created_at: string | null
  approved_at: string | null
}

export function getAccessToken(): string {
  return localStorage.getItem('access_token') ?? ''
}

export async function uploadTempImage(file: File): Promise<{ image_url: string; file_path: string }> {
  const formData = new FormData()
  formData.append('image', file)
  const { data } = await client.post('/api/chat/upload-image/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export interface UploadedDocumentResult {
  document_id: string
  file_path: string
  file_name: string
  mime_type: string
  char_count: number
  /** 'ok' — text read straight from the file.
   *  'ocr_recovered' — no text layer; transcribed from the page images,
   *    so the values need a human check.
   *  'empty' — nothing readable at all. Show `warning` immediately; the
   *    agent cannot see this file's contents. */
  extraction_status?: 'ok' | 'ocr_recovered' | 'empty'
  /** Plain-English explanation to show the user. Empty when status is 'ok'. */
  warning?: string
}

export async function uploadTempDocument(file: File): Promise<UploadedDocumentResult> {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await client.post('/api/chat/upload-document/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

/** Streamed SSE event callbacks. Only onChunk/onDone/onError are required;
 *  the rest fire for their respective tool/feature events. Passing handlers
 *  by name (not position) means a missing/misnamed one is a compile error. */
export interface StreamHandlers {
  onChunk: (text: string) => void
  onDone: (data: StreamDoneData) => void
  onError: (error: string, errorData?: Record<string, unknown>) => void
  // A tool call (usually generate_document) was cut off mid-write by the
  // output-token ceiling, so it never ran. Not a terminal event — the
  // stream still finishes normally with 'done'.
  onDocumentTruncated?: (data: { tool: string }) => void
  onImageGenerating?: (data: ImageGeneratingData) => void
  onImageComplete?: (data: ImageCompleteData) => void
  onSocialPostDrafting?: (data: SocialPostDraftingData) => void
  onSocialPostReady?: (data: SocialPostReadyData) => void
  onContentThemeProposal?: (data: ContentThemeProposalData) => void
  onBrandKitProposal?: (data: BrandKitProposalData) => void
  onAgentCustomizationProposal?: (data: AgentCustomizationProposalData) => void
  onAdLibraryResults?: (data: AdLibraryResultsData) => void
  onLinkedInEventCreated?: (data: LinkedInEventCreatedData) => void
  onLinkedInEventsList?: (data: LinkedInEventsListData) => void
  onSocialReadStarting?: (data: SocialReadStartingData) => void
  onSocialReadComplete?: (data: SocialReadCompleteData) => void
  onCommentReplyDrafting?: (data: CommentReplyDraftingData) => void
  onCommentReplyReady?: (data: CommentReplyReadyData) => void
  onCalendarEventDrafting?: (data: CalendarEventDraftingData) => void
  onCalendarEventReady?: (data: CalendarEventReadyData) => void
  onGmailDraftDrafting?: (data: GmailDraftDraftingData) => void
  onGmailDraftReady?: (data: GmailDraftReadyData) => void
  onLinkedInAdReportLoading?: (data: LinkedInAdReportLoadingData) => void
  onLinkedInAdReportReady?: (data: LinkedInAdReportReadyData) => void
  onLinkedInAdReportFailed?: (data: LinkedInAdReportFailedData) => void
  onDocumentGenerating?: (data: DocumentGeneratingData) => void
  onDocumentComplete?: (data: DocumentCompleteData) => void
  onSpreadsheetGenerating?: (data: SpreadsheetGeneratingData) => void
  onSpreadsheetComplete?: (data: SpreadsheetCompleteData) => void
  // Resolves the matching image/document/spreadsheet "generating"
  // placeholder so a card never spins forever after a failed generation.
  onGenerationFailed?: (kind: 'image' | 'document' | 'spreadsheet', error: string) => void
  // Stripe Connect — pending refund proposed by an agent. The card
  // renders immediately in the chat and stays put until the user
  // approves (with the mandatory checkbox) or rejects.
  onStripeActionPending?: (data: StripeActionPendingData) => void
}

export interface StreamOptions {
  // Stop button — when aborted, fetch + reader.read() reject with
  // AbortError, surfaced as onError('cancelled').
  signal?: AbortSignal
  // Attached images / documents. Combined, capped at 5 per message.
  uploadedImages?: Array<{ base64: string; mediaType: string; url: string }>
  uploadedDocuments?: Array<{ document_id: string; file_name: string; mime_type: string; char_count: number }>
  // Handoff continuation — reuse the pre-seeded user message, skip the
  // @mention scanner for this turn.
  isContinuationResume?: boolean
}

export interface SendMessageStreamParams {
  sessionId: string
  message: string
  agentId?: string
  handlers: StreamHandlers
  options?: StreamOptions
}

export async function sendMessageStream(params: SendMessageStreamParams): Promise<void> {
  const { sessionId, message, agentId, handlers, options = {} } = params
  const {
    onChunk, onDone, onError, onImageGenerating, onImageComplete,
    onSocialPostDrafting, onSocialPostReady, onContentThemeProposal,
    onBrandKitProposal, onAgentCustomizationProposal, onAdLibraryResults,
    onLinkedInEventCreated, onLinkedInEventsList, onSocialReadStarting,
    onSocialReadComplete, onCommentReplyDrafting, onCommentReplyReady,
    onCalendarEventDrafting, onCalendarEventReady, onGmailDraftDrafting,
    onGmailDraftReady, onLinkedInAdReportLoading, onLinkedInAdReportReady,
    onLinkedInAdReportFailed, onDocumentGenerating, onDocumentComplete,
    onSpreadsheetGenerating, onSpreadsheetComplete, onGenerationFailed,
    onStripeActionPending, onDocumentTruncated,
  } = handlers
  const { signal, uploadedImages, uploadedDocuments, isContinuationResume } = options
  let response: globalThis.Response
  try {
    // Build the array body field if the caller passed one; otherwise let
    // the backend fall back to the legacy single-image fields below.
    const uploadedImagesBody = (uploadedImages ?? []).map((img) => ({
      base64: img.base64,
      media_type: img.mediaType,
      url: img.url,
    }))
    const uploadedDocumentsBody = (uploadedDocuments ?? []).map((doc) => ({
      document_id: doc.document_id,
      file_name: doc.file_name,
      mime_type: doc.mime_type,
      char_count: doc.char_count,
    }))
    const streamBody = JSON.stringify({
      session_id: sessionId,
      message,
      agent_id: agentId ?? null,
      // Legacy single-image fields — retained as empty strings so the
      // backend request shape is unchanged; uploaded_images is the
      // sole image-input path now.
      image_base64: '',
      image_media_type: '',
      image_url: '',
      uploaded_images: uploadedImagesBody,
      uploaded_documents: uploadedDocumentsBody,
      is_continuation_resume: isContinuationResume ?? false,
    })
    // This path bypasses the axios 401 interceptor, so handle token freshness
    // explicitly: proactively refresh a near-expiry token before opening the
    // stream, then retry once on a 401 with a force-refreshed token.
    const openStream = (token: string) =>
      fetch('/api/agents/chat/stream/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: streamBody,
        signal,
      })
    response = await openStream(await ensureValidAccessToken())
    if (response.status === 401) {
      try {
        response = await openStream(await forceRefreshAccessToken())
      } catch {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
        return
      }
    }
  } catch (e: unknown) {
    if (e instanceof Error && e.name === 'AbortError') {
      onError('cancelled')
    } else {
      onError('network_error')
    }
    return
  }

  if (!response.ok) {
    try {
      const errData = await response.json()
      onError(errData.error ?? 'request_failed', errData)
    } catch {
      onError('request_failed')
    }
    return
  }

  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  // Tracks whether a terminal event (done/error) was seen. If the stream
  // closes without one — server killed, proxy timeout — we synthesize an
  // error so the UI never strands a half-rendered message.
  let sawTerminal = false

  const handleLine = (line: string) => {
    if (!line.startsWith('data: ')) return
    const jsonStr = line.slice(6).trim()
    if (!jsonStr) return
    try {
      const event = JSON.parse(jsonStr)
      if (event.type === 'done' || event.type === 'error') sawTerminal = true
      dispatchEvent(event)
    } catch { /* malformed JSON, skip */ }
  }

  while (true) {
    let chunkResult: ReadableStreamReadResult<Uint8Array>
    try {
      chunkResult = await reader.read()
    } catch (e: unknown) {
      // Stop button — reader.read() rejects with AbortError after the
      // signal fires. Surface as 'cancelled' so the hook knows to
      // mark the in-flight assistant message as cancelled instead of
      // wiping it like a network error would. Any other read error is a
      // network drop — route it through onError, never let it escape as
      // an unhandled promise rejection (the caller has no catch).
      if (e instanceof Error && e.name === 'AbortError') {
        onError('cancelled')
      } else {
        onError('network_error')
      }
      return
    }
    const { done, value } = chunkResult
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''
    for (const line of lines) handleLine(line)
  }

  // Flush any residual buffered frame (a final event split across the
  // last TCP segments with no trailing newline).
  buffer += decoder.decode()
  if (buffer.trim()) handleLine(buffer)

  // The stream ended without a done/error event — treat as a failure so
  // the placeholder message gets a terminal state instead of spinning.
  if (!sawTerminal) onError('stream_closed')

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function dispatchEvent(event: any) {
    {
        if (event.type === 'chunk') onChunk(event.text)
        // 'text' — a one-shot server-composed message (e.g. a tool-error
        // explanation). Same handling as a streamed chunk so it isn't lost.
        else if (event.type === 'text') onChunk(event.text ?? '')
        else if (event.type === 'done') onDone(event as StreamDoneData)
        else if (event.type === 'error') onError(event.error)
        else if (event.type === 'image_generating') onImageGenerating?.({ prompt: event.prompt ?? '' })
        else if (event.type === 'image_complete') onImageComplete?.({ url: event.url, width: event.width, height: event.height })
        else if (event.type === 'social_post_drafting') onSocialPostDrafting?.({ caption: event.caption ?? '' })
        else if (event.type === 'social_post_ready') onSocialPostReady?.(event as SocialPostReadyData)
        else if (event.type === 'content_theme_proposal') onContentThemeProposal?.(event as ContentThemeProposalData)
        else if (event.type === 'brand_kit_proposal') onBrandKitProposal?.(event as BrandKitProposalData)
        else if (event.type === 'agent_customization_proposal') onAgentCustomizationProposal?.(event as AgentCustomizationProposalData)
        else if (event.type === 'ad_library_results') onAdLibraryResults?.(event as AdLibraryResultsData)
        else if (event.type === 'linkedin_event_created') onLinkedInEventCreated?.(event as LinkedInEventCreatedData)
        else if (event.type === 'linkedin_events_list') onLinkedInEventsList?.(event as LinkedInEventsListData)
        else if (event.type === 'linkedin_ad_report_loading') onLinkedInAdReportLoading?.(event as LinkedInAdReportLoadingData)
        else if (event.type === 'linkedin_ad_report_ready') onLinkedInAdReportReady?.(event as LinkedInAdReportReadyData)
        else if (event.type === 'linkedin_ad_report_failed') onLinkedInAdReportFailed?.(event as LinkedInAdReportFailedData)
        else if (event.type === 'social_read_starting') onSocialReadStarting?.(event as SocialReadStartingData)
        else if (event.type === 'social_read_complete') onSocialReadComplete?.(event as SocialReadCompleteData)
        else if (event.type === 'comment_reply_drafting') onCommentReplyDrafting?.(event as CommentReplyDraftingData)
        else if (event.type === 'comment_reply_ready') onCommentReplyReady?.(event as CommentReplyReadyData)
        else if (event.type === 'calendar_event_drafting') onCalendarEventDrafting?.({ summary: event.summary ?? '' })
        else if (event.type === 'calendar_event_ready') onCalendarEventReady?.(event as CalendarEventReadyData)
        else if (event.type === 'gmail_draft_drafting') onGmailDraftDrafting?.({ subject: event.subject ?? '' })
        else if (event.type === 'gmail_draft_ready') onGmailDraftReady?.(event as GmailDraftReadyData)
        else if (event.type === 'document_generating') onDocumentGenerating?.({ title: event.title ?? '' })
        else if (event.type === 'document_complete') onDocumentComplete?.({ id: event.id, url: event.url, title: event.title ?? '', file_name: event.file_name ?? '', byte_size: event.byte_size ?? 0 })
        else if (event.type === 'spreadsheet_generating') onSpreadsheetGenerating?.({ title: event.title ?? '' })
        else if (event.type === 'spreadsheet_complete') onSpreadsheetComplete?.({ id: event.id, url: event.url, title: event.title ?? '', file_name: event.file_name ?? '', byte_size: event.byte_size ?? 0 })
        else if (event.type === 'stripe_action_pending') onStripeActionPending?.(event as StripeActionPendingData)
        else if (event.type === 'image_failed') onGenerationFailed?.('image', event.error ?? '')
        else if (event.type === 'document_failed') onGenerationFailed?.('document', event.error ?? '')
        else if (event.type === 'spreadsheet_failed') onGenerationFailed?.('spreadsheet', event.error ?? '')
        else if (event.type === 'document_truncated') onDocumentTruncated?.({ tool: event.tool ?? '' })
    }
  }
}
